/**
 * The pad voices — the measured synthesis recipes, plus the two tanks and the siren.
 *
 * Every number in this file is a **measurement**, not a preference. The recipes were
 * built and listened to in a Chromium prototype before any of it was ported, and the
 * comments record what was heard so that the next person does not "tidy" a value that is
 * load-bearing. If a value looks arbitrary, it is because an ear put it there.
 *
 * ## Why these seven are synthesised and the rest are files
 *
 * A dancehall riddim's drums were themselves built on drum machines — a TR-808's cowbell
 * is two squares at 540 and 800 Hz through a bandpass, and those are the schematic
 * values, so {@link cowbell} is not an imitation of the sound, it **is** the sound.
 * Sampling a sample of a synthesiser would be a downgrade with a licence attached. So the
 * seven `synth:<voice>` assignments of the pad contract are the correct instrument for
 * their pads, not a placeholder for a file that never arrived.
 *
 * The horn, the whistle and the pull-up calls are the other way round: they are recordings
 * of air moving through brass and plastic, synthesis of them reads as a toy, and they come
 * from the server-side CC0 library instead. {@link lickShot} sits on the boundary and is
 * deliberately a **stylised slap**, not a firearm: a real gunshot is a supersonic
 * shockwave in a specific room and no oscillator gets near it.
 *
 * ## Four things that are load-bearing and were learned the hard way
 *
 * 1. **{@link driveCurve} is peak-normalised, so a waveshaper's small-signal slope is
 *    `k / tanh(k)` — a gain, not unity.** Inside the echo's feedback path that turned
 *    0.68 of feedback into an effective 1.53 and the tank **self-oscillated**: measured,
 *    one click grew 0.24 → 0.41 → 0.55 and then parked on 0.68 forever. The fix is the
 *    `tanh(k)/k` trim gain *into* the shaper in {@link tapeEcho}. **Always attenuate into
 *    a shaper, never after it** — after it you have already clipped.
 * 2. **A tank used as a SEND must be wet-only.** As an insert (a zap, the siren) dry+wet
 *    is right. As a send the dry path doubles the signal and the result reads as +6 dB,
 *    not as an echo. Hence {@link EchoOptions.wetOnly}, and hence the test that walks the
 *    graph rather than trusting the flag.
 * 3. **Schedule at `ctx.currentTime + 0.02`, never at `ctx.currentTime`.** The current
 *    render quantum is already being rendered, so anything scheduled inside it lands
 *    late and the attack — the only part of a one-shot anyone hears — gets clipped. The
 *    lead lives in `engine.ts` as `SCHEDULE_LEAD_S`; every function here takes `time`
 *    already offset and never reads the clock itself.
 * 4. **Build every cached buffer on the gesture that creates the context, not on the
 *    first tap.** Done lazily it was measured at ~19 ms of main-thread jank, i.e. a
 *    visibly late first hit. {@link warmVoiceCaches} is the eager pass and
 *    `engine.ts` calls it synchronously while the context is being built.
 *
 * ## Teardown runs on the audio clock, not on `setTimeout`
 *
 * {@link sched} schedules a silent `ConstantSourceNode` and hangs the cleanup off its
 * `ended` event. A backgrounded tab must not be able to orphan a feedback loop, and a
 * timer is the wrong clock for a job measured in samples — see also spec.md §13.3, which
 * bans `requestAnimationFrame` for anything that has to survive a hidden page.
 *
 * @see docs/design-direction.md §7.1 — 333 ms is `--t8`, an eighth at 90 BPM. The echo
 *      and the UI are on the same grid on purpose.
 */

import type { SynthVoice } from '@bl/shared';

/* ══ what a voice is ══════════════════════════════════════════════════════════════ */

/**
 * A one-shot.
 *
 * Builds its own graph into `dest`, schedules everything relative to `time` — which the
 * caller has **already** offset by the render-quantum lead — and returns how long it will
 * sound for, in seconds. The return value is what drives the pad's ink flip
 * (design-direction.md §7.2 INKING) and the engine's voice accounting; it is not an
 * estimate, it is the schedule the function just wrote.
 *
 * Nothing here disconnects itself unless it holds a feedback loop. Web Audio drops a node
 * as soon as it has finished sounding and nothing references it, and a `disconnect()` per
 * one-shot is main-thread work in the one place we cannot afford any.
 */
export type PadVoice = (ctx: BaseAudioContext, dest: AudioNode, time: number) => number;

/* ══ deterministic noise ══════════════════════════════════════════════════════════ */

/**
 * A seeded PRNG, so every hit of a pad is byte-identical to the last one.
 *
 * `Math.random()` would make a snare that is subtly different every strike, which sounds
 * *worse*, not more human: the ear hears the variation as the sample being broken rather
 * than as a drummer. It also makes the buffers untestable.
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Every noise seed the seven voices ask for, so {@link warmVoiceCaches} can build the lot
 * on the gesture instead of on the first tap (lesson 4 in the module header).
 *
 * The seeds themselves are arbitrary; that they are *fixed* is not.
 */
export const NOISE_SEEDS: readonly number[] = [
  0x9e3779b9, // the default, shared by every voice that does not ask for its own
  0x51ed270b, // lickShot crack
  0x2f8a1c33, // lickShot report
  0x7c3af1d9, // snare wires
  0x1b9e77c1, // snare rim crack
  0x4a7d2e15, // cowbell beater
  0x8f2b4d17, // tom head
];

/** Two seconds is long enough that no voice ever runs off the end of a noise buffer. */
export const NOISE_BUFFER_S = 2;

const noiseCache = new Map<string, AudioBuffer>();

/**
 * A cached mono buffer of seeded white noise.
 *
 * Keyed by sample rate as well as length and seed: the buffer is rendered at the
 * context's rate, and a cache that outlived its context would hand the next one
 * resampled garbage. {@link resetVoiceCaches} drops these on teardown for the same
 * reason.
 */
export function noiseBuffer(
  ctx: BaseAudioContext,
  seconds: number = NOISE_BUFFER_S,
  seed: number = 0x9e3779b9,
): AudioBuffer {
  const key = `${String(ctx.sampleRate)}:${String(seconds)}:${String(seed)}`;
  const hit = noiseCache.get(key);
  if (hit !== undefined) return hit;

  const frames = Math.max(1, Math.ceil(seconds * ctx.sampleRate));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  const rnd = mulberry32(seed);
  for (let i = 0; i < frames; i += 1) data[i] = rnd() * 2 - 1;

  noiseCache.set(key, buffer);
  return buffer;
}

/**
 * How far the read head walks per hit, as a fraction of a turn.
 *
 * 0.137 is coprime-ish with 1 so the walk does not repeat for a long time. It exists
 * because sixteen hits that all start at sample 0 of the same buffer phase-lock and the
 * roll turns into one long comb-filtered buzz.
 */
const NOISE_TURN_STEP = 0.137;
let noiseTurn = 0;

/** A started `AudioBufferSourceNode` over the cached noise, offset a little each time. */
export function noiseSource(
  ctx: BaseAudioContext,
  time: number,
  duration: number,
  seed?: number,
): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, NOISE_BUFFER_S, seed);
  noiseTurn = (noiseTurn + NOISE_TURN_STEP) % 1;
  src.start(time, noiseTurn * 1.5, duration + 0.02);
  src.stop(time + duration + 0.02);
  return src;
}

/* ══ waveshaping — attenuate INTO a shaper, never after it ════════════════════════ */

/** The default curve table length. 2048 points is inaudibly fine at 4× oversampling. */
export const CURVE_POINTS = 2048;

const curveCache = new Map<string, Float32Array<ArrayBuffer>>();

/**
 * A `tanh` drive curve, **peak-normalised** so that ±1 in maps to ±1 out.
 *
 * The `0.06 * x²` term is second-harmonic asymmetry — the reason a valve sounds different
 * from a diode — and it leaves DC on the output, which is why every voice that uses a
 * shaper has a highpass after it.
 *
 * **The trap.** Peak normalisation divides by `tanh(k)`, so the slope near zero is
 * `k / tanh(k)`: at `k = 2.2` that is 2.25× — a shaper that is a *gain* for everything
 * quiet. Put one inside a feedback loop and the loop gain is not the number you wrote.
 * See {@link tapeEcho}, where it self-oscillated.
 */
export function driveCurve(k: number, points: number = CURVE_POINTS): Float32Array<ArrayBuffer> {
  const key = `${String(k)}:${String(points)}`;
  const hit = curveCache.get(key);
  if (hit !== undefined) return hit;

  const curve = new Float32Array(points);
  const norm = Math.tanh(k);
  for (let i = 0; i < points; i += 1) {
    const x = (i / (points - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * (x + 0.06 * x * x)) / norm;
  }
  curveCache.set(key, curve);
  return curve;
}

/** The small-signal gain a peak-normalised {@link driveCurve} applies: `k / tanh(k)`. */
export function driveSmallSignalGain(k: number): number {
  return k / Math.tanh(k);
}

/**
 * The trim that cancels {@link driveSmallSignalGain}, i.e. `tanh(k) / k`.
 *
 * Belongs on the node **before** the shaper. This is the whole fix for the runaway echo
 * described in the module header, and it is a function rather than a literal so that
 * changing a drive constant cannot silently un-fix it.
 */
export function driveTrimGain(k: number): number {
  return Math.tanh(k) / k;
}

/** A `WaveShaperNode` at drive `k`, 4× oversampled to keep the aliasing out. */
export function shaper(ctx: BaseAudioContext, k: number): WaveShaperNode {
  const ws = ctx.createWaveShaper();
  ws.curve = driveCurve(k);
  ws.oversample = '4x';
  return ws;
}

/** Where the bus limiter stops being linear. Below this it is a wire. */
export const LIMITER_KNEE = 0.7;

/**
 * The limiter table is sample-rate independent — it is arithmetic on a number line, not
 * audio — so unlike the noise buffers it survives a context swap and is never reset.
 */
let limitCurve: Float32Array<ArrayBuffer> | null = null;

/**
 * A soft-knee limiter curve: linear to {@link LIMITER_KNEE}, `tanh` above it.
 *
 * Sixteen pads plus a siren plus a 0.68-feedback echo can and will ask for more than full
 * scale at the same time. Without this the sum hard-clips in the output stage and the
 * whole bank turns to fuzz for as long as the overlap lasts.
 */
export function limiterCurve(
  points = 4096,
  knee: number = LIMITER_KNEE,
): Float32Array<ArrayBuffer> {
  if (limitCurve !== null) return limitCurve;
  const curve = new Float32Array(points);
  for (let i = 0; i < points; i += 1) {
    const x = (i / (points - 1)) * 2 - 1;
    const a = Math.abs(x);
    curve[i] =
      a <= knee ? x : Math.sign(x) * (knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee)));
  }
  limitCurve = curve;
  return curve;
}

/* ══ graph plumbing ══════════════════════════════════════════════════════════════ */

/** A `GainNode` with its gain already set — the single most repeated line in this file. */
export function gain(ctx: BaseAudioContext, value = 1): GainNode {
  const node = ctx.createGain();
  node.gain.value = value;
  return node;
}

/** A `BiquadFilterNode`, configured. `Q` defaults to Butterworth. */
export function biquad(
  ctx: BaseAudioContext,
  type: BiquadFilterType,
  frequency: number,
  q = 0.707,
  dbGain = 0,
): BiquadFilterNode {
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = frequency;
  filter.Q.value = q;
  filter.gain.value = dbGain;
  return filter;
}

/**
 * Connects nodes head to tail and returns the tail, so a serial chain reads as one line.
 *
 * Only for node→node hops. A connection into an `AudioParam` returns `void` and is
 * written out longhand at the call site, which is also where it deserves a comment.
 */
export function chain<T extends AudioNode>(first: AudioNode, ...rest: [...AudioNode[], T]): T {
  const nodes: AudioNode[] = [first, ...rest];
  for (let i = 0; i < nodes.length - 1; i += 1) {
    const from = nodes[i];
    const to = nodes[i + 1];
    if (from === undefined || to === undefined) continue;
    from.connect(to);
  }
  return rest[rest.length - 1] as T;
}

/** A bus: an input, a soft limiter, and an output trim. */
export interface PadBus {
  /** Everything that plays on this bus connects here. */
  readonly input: GainNode;
  /** The post-limiter trim. The only gain a duck or a mute should ever touch. */
  readonly out: GainNode;
}

/**
 * Sub-unity output trim, so the limiter has somewhere to work.
 *
 * At 1.0 the limiter's own knee is the ceiling and inter-sample peaks still clip in the
 * output stage; 0.95 is about 0.45 dB of headroom for free.
 */
export const BUS_TRIM = 0.95;

/** Builds a limited bus into `destination`. */
export function bus(ctx: BaseAudioContext, destination: AudioNode): PadBus {
  const input = gain(ctx, 1);
  const limiter = ctx.createWaveShaper();
  limiter.curve = limiterCurve();
  // 2x, not 4x: this one runs for the whole session on every sample, and the difference
  // was inaudible on the bus while the CPU cost was not.
  limiter.oversample = '2x';
  const out = gain(ctx, BUS_TRIM);
  chain(input, limiter, out).connect(destination);
  return { input, out };
}

/* ══ envelopes ═══════════════════════════════════════════════════════════════════ */

/**
 * Attack then exponential decay to silence.
 *
 * `peak` must be `> 0`: `exponentialRampToValueAtTime` cannot ramp to or through zero, so
 * the tail lands on `peak * 1e-4` (−80 dB) and is then cut with a `setValueAtTime`. That
 * final write is not decoration — without it the param holds −80 dB forever and sixteen
 * pads leave sixteen DC-ish trickles on the bus.
 */
export function perc(param: AudioParam, t: number, attack: number, decay: number, peak = 1): void {
  param.setValueAtTime(0, t);
  param.linearRampToValueAtTime(peak, t + attack);
  param.exponentialRampToValueAtTime(peak * 1e-4, t + attack + decay);
  param.setValueAtTime(0, t + attack + decay);
}

/** Attack, hold, release — for anything that is blown or bowed rather than struck. */
export function ahr(
  param: AudioParam,
  t: number,
  attack: number,
  hold: number,
  release: number,
  peak = 1,
): void {
  param.setValueAtTime(0, t);
  param.linearRampToValueAtTime(peak, t + attack);
  param.setValueAtTime(peak, t + attack + hold);
  param.exponentialRampToValueAtTime(peak * 1e-4, t + attack + hold + release);
  param.setValueAtTime(0, t + attack + hold + release);
}

/**
 * Two-stage decay: a fast one to `knee`, then a slow tail.
 *
 * This is the difference between a snare and a TR-707. A single exponential on the wires
 * is the machine sound; real wires dump most of their energy immediately and then ring.
 */
export function perc2(
  param: AudioParam,
  t: number,
  attack: number,
  fast: number,
  knee: number,
  slow: number,
  peak = 1,
): void {
  param.setValueAtTime(0, t);
  param.linearRampToValueAtTime(peak, t + attack);
  param.exponentialRampToValueAtTime(peak * knee, t + attack + fast);
  param.exponentialRampToValueAtTime(peak * 1e-4, t + attack + fast + slow);
  param.setValueAtTime(0, t + attack + fast + slow);
}

/**
 * Runs `fn` at `time` **on the audio clock**.
 *
 * A silent `ConstantSourceNode` is started and stopped, and the cleanup hangs off its
 * `ended` event. `setTimeout` was the obvious alternative and is wrong: a backgrounded
 * tab throttles timers, and the thing being cleaned up here is usually a feedback loop
 * that must not be left running (see {@link springTank} inside {@link lickShot}). Same
 * reasoning as spec.md §13.3's ban on `requestAnimationFrame` for work that has to
 * survive a hidden page — pick the clock that matches the job.
 */
export function sched(ctx: BaseAudioContext, time: number, fn: () => void): void {
  const marker = ctx.createConstantSource();
  marker.offset.value = 0;
  marker.connect(ctx.destination);
  marker.onended = (): void => {
    marker.disconnect();
    fn();
  };
  marker.start(time);
  marker.stop(time + 0.001);
}

/* ══ the tape echo tank ══════════════════════════════════════════════════════════ */

export interface EchoOptions {
  /** Delay in seconds. 0.333 is an eighth at 90 BPM — `--t8` (design-direction.md §7.1). */
  readonly delay?: number;
  /** Feedback, as a linear gain. 0.68 is four seconds of tail. */
  readonly feedback?: number;
  /** Repeat lowpass corner. Each pass gets darker, which is what tape does. */
  readonly tone?: number;
  /** **Send use requires `true`.** See lesson 2 in the module header. */
  readonly wetOnly?: boolean;
}

export interface EchoTank {
  readonly input: GainNode;
  readonly out: GainNode;
  readonly delay: DelayNode;
  /** The repeats knob. Wind it down to end a tail without cutting it. */
  readonly feedback: GainNode;
  /** Wow and flutter. Must be stopped on teardown or they run forever. */
  readonly modulators: readonly OscillatorNode[];
}

/** The drive inside the echo's feedback path. See {@link driveTrimGain}. */
export const ECHO_SATURATION_K = 2.2;

/**
 * A tape echo: delay, darkening lowpass, a highpass to keep the low end from stacking, a
 * saturator, and wow plus flutter.
 *
 * **The self-oscillation.** With the saturator in the loop and no trim, 0.68 of written
 * feedback became an effective 1.53, because {@link driveCurve} is peak-normalised and
 * its small-signal slope at `k = 2.2` is 2.25. Measured: a single click grew 0.24 → 0.41
 * → 0.55 and then sat on 0.68 indefinitely — a tank that never stopped ringing. The
 * `satTrim` gain **before** the shaper is the fix. Attenuate into a shaper; after it the
 * damage is done.
 *
 * The 190 Hz highpass is the other half of not-a-digital-delay: without it every repeat
 * adds bass and the tank turns to mud by the fourth one.
 */
export function tapeEcho(ctx: BaseAudioContext, time: number, options: EchoOptions = {}): EchoTank {
  const delaySeconds = options.delay ?? 0.333;
  const feedbackGain = options.feedback ?? 0.62;
  const tone = options.tone ?? 2400;
  const wetOnly = options.wetOnly === true;

  const input = gain(ctx, 1);
  const out = gain(ctx, 1);
  const delay = ctx.createDelay(1.5);
  delay.delayTime.value = delaySeconds;
  const lp = biquad(ctx, 'lowpass', tone, 0.6);
  const hp = biquad(ctx, 'highpass', 190, 0.6);
  const satTrim = gain(ctx, driveTrimGain(ECHO_SATURATION_K));
  const sat = shaper(ctx, ECHO_SATURATION_K);
  const feedback = gain(ctx, feedbackGain);

  // Wow (0.55 Hz) and flutter (6.3 Hz) are the difference between a Space Echo and a
  // digital delay. The depths are in seconds of delay time: 1.6 ms and 0.18 ms.
  const wow = ctx.createOscillator();
  wow.type = 'sine';
  wow.frequency.value = 0.55;
  const wowDepth = gain(ctx, 0.0016);
  const flutter = ctx.createOscillator();
  flutter.type = 'sine';
  flutter.frequency.value = 6.3;
  const flutterDepth = gain(ctx, 0.00018);
  wow.connect(wowDepth).connect(delay.delayTime);
  flutter.connect(flutterDepth).connect(delay.delayTime);
  wow.start(time);
  flutter.start(time);

  input.connect(delay);
  chain(delay, lp, hp, satTrim, sat, feedback).connect(delay);
  delay.connect(out);
  // The dry path exists for an INSERT and must not exist for a SEND: on a send the bus is
  // already carrying the dry signal, so a second copy here reads as +6 dB of pads, not as
  // an echo. Lesson 2.
  if (!wetOnly) input.connect(out);

  return { input, out, delay, feedback, modulators: [wow, flutter] };
}

/* ══ the spring tank ═════════════════════════════════════════════════════════════ */

export interface SpringOptions {
  /** One traversal of the spring, in seconds. */
  readonly travel?: number;
  readonly feedback?: number;
  /** Allpass stages. This number *is* the dispersion. */
  readonly stages?: number;
}

export interface SpringTank {
  readonly input: GainNode;
  readonly out: GainNode;
  readonly feedback: GainNode;
}

/**
 * A spring reverb.
 *
 * A spring is a **dispersive** delay: high frequencies travel faster than low ones, so a
 * transient arrives smeared into a descending chirp. That is why this is an allpass
 * cascade and not a room simulation — the pre-ring and the chirp fall out of the physics
 * for free, and no amount of early-reflection tuning gets there without them.
 *
 * The bandpass at 1.9 kHz is the transducer, which is a small coil of wire and hears
 * almost nothing outside its own band.
 */
export function springTank(
  ctx: BaseAudioContext,
  _time: number,
  options: SpringOptions = {},
): SpringTank {
  const travel = options.travel ?? 0.042;
  const feedbackGain = options.feedback ?? 0.74;
  const stages = options.stages ?? 12;

  const input = gain(ctx, 1);
  const out = gain(ctx, 1);
  const delay = ctx.createDelay(0.5);
  delay.delayTime.value = travel;
  const band = biquad(ctx, 'bandpass', 1900, 0.9);
  const feedback = gain(ctx, feedbackGain);
  const allpasses: BiquadFilterNode[] = [];
  for (let i = 0; i < stages; i += 1) {
    allpasses.push(biquad(ctx, 'allpass', 700 + i * 210, 3.2));
  }

  input.connect(delay);
  chain(delay, band, ...allpasses, feedback).connect(delay);
  delay.connect(out);
  return { input, out, feedback };
}

/* ══ the seven synth voices ══════════════════════════════════════════════════════ */

/**
 * `synth:shot` — LICK SHOT.
 *
 * The honest version: a stylised crack, not a firearm. Synthesis cannot make a real
 * gunshot convincing — a real one is a supersonic shockwave in a specific room — so this
 * is deliberately a hard slap: a highpassed crack through a very hard shaper, a
 * band-limited report, a low thump for the chest, and 24 ms of spring for the yard.
 *
 * The 24 ms slap is the sound of the dance itself and is the reason this reads as a
 * soundsystem and not as a foley library.
 */
export function lickShot(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 0.32;
  const bang = gain(ctx, 1.15);
  bang.connect(dest);

  const crack = noiseSource(ctx, time, 0.03, 0x51ed270b);
  const crackHp = biquad(ctx, 'highpass', 3800, 0.7);
  const crackGain = gain(ctx, 0);
  chain(crack, crackHp, shaper(ctx, 40), crackGain).connect(bang);
  perc(crackGain.gain, time, 0.0003, 0.012, 0.28);

  const report = noiseSource(ctx, time, 0.12, 0x2f8a1c33);
  const reportBp = biquad(ctx, 'bandpass', 900, 0.8);
  const reportGain = gain(ctx, 0);
  chain(report, reportBp, shaper(ctx, 6), reportGain).connect(bang);
  perc(reportGain.gain, time, 0.0006, 0.1, 0.5);

  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(180, time);
  thump.frequency.exponentialRampToValueAtTime(45, time + 0.09);
  const thumpGain = gain(ctx, 0);
  chain(thump, thumpGain).connect(bang);
  perc(thumpGain.gain, time, 0.001, 0.16, 0.6);
  thump.start(time);
  thump.stop(time + duration);

  const slap = springTank(ctx, time, { travel: 0.024, feedback: 0.42, stages: 6 });
  const slapWet = gain(ctx, 0.5);
  chain(slap.out, slapWet).connect(dest);
  reportGain.connect(slap.input);
  // 0.42 of feedback in a 24 ms loop rings for well over a second. Wound down on the
  // audio clock so a hidden tab cannot leave it running.
  sched(ctx, time + duration, () => {
    slap.feedback.gain.value = 0;
    slapWet.disconnect();
  });

  return duration;
}

/**
 * `synth:one-drop` — the ONE DROP KICK.
 *
 * Deep and felt-muffled, with no click at all. The 250 Hz lowpass is what makes it read
 * as "dead" rather than as an 808, and the beater noise is under it, not over it.
 *
 * The saturated ghost above 130 Hz is not a colour choice. A phone speaker reproduces
 * nothing at 46 Hz, so the ear is given the harmonics it needs to *infer* a fundamental
 * the device cannot make. Remove it and the pad is silent on the hardware most of the
 * audience is holding.
 */
export function oneDropKick(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 0.28;
  const lp = biquad(ctx, 'lowpass', 250, 0.7);
  const sat = shaper(ctx, 2.4);
  chain(lp, sat).connect(dest);

  const body = ctx.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(120, time);
  body.frequency.exponentialRampToValueAtTime(46, time + 0.055);
  body.frequency.exponentialRampToValueAtTime(42, time + 0.3);
  const bodyGain = gain(ctx, 0);
  chain(body, bodyGain).connect(lp);
  perc(bodyGain.gain, time, 0.002, 0.26, 0.8);
  body.start(time);
  body.stop(time + duration);

  const knock = ctx.createOscillator();
  knock.type = 'sine';
  knock.frequency.value = 92;
  const knockGain = gain(ctx, 0);
  chain(knock, knockGain).connect(lp);
  perc(knockGain.gain, time, 0.002, 0.09, 0.3);
  knock.start(time);
  knock.stop(time + duration);

  // The audible ghost, tapped off the body envelope so it tracks it exactly.
  const ghost = shaper(ctx, 7);
  const ghostHp = biquad(ctx, 'highpass', 130, 0.7);
  const ghostLp = biquad(ctx, 'lowpass', 900, 0.7);
  const ghostGain = gain(ctx, 0.45);
  chain(bodyGain, ghost, ghostHp, ghostLp, ghostGain).connect(dest);

  const beater = noiseSource(ctx, time, 0.03);
  const beaterLp = biquad(ctx, 'lowpass', 220, 0.7);
  const beaterGain = gain(ctx, 0);
  chain(beater, beaterLp, beaterGain).connect(lp);
  perc(beaterGain.gain, time, 0.0005, 0.02, 0.5);

  return duration;
}

/**
 * `synth:snare` — the ONE DROP SNARE.
 *
 * Shell tuned low, at 185 and 278 Hz — a rockers snare is tuned up, a one drop snare is
 * not — with a two-stage wire decay ({@link perc2}, and a one-stage decay is audibly a
 * TR-707) and a clipped rim crack on top.
 */
export function oneDropSnare(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 0.32;
  const pre = gain(ctx, 0.5);
  const sat = shaper(ctx, 2.2);
  const glue = biquad(ctx, 'highpass', 120, 0.7);
  const trim = gain(ctx, 1.15);
  chain(pre, sat, glue, trim).connect(dest);

  const modes: readonly (readonly [number, number, number])[] = [
    [185, 0.18, 0.55],
    [278, 0.12, 0.28],
  ];
  for (const [frequency, decay, peak] of modes) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(frequency, time);
    // The shell detunes DOWN as the head relaxes. Up would be a tom being tuned.
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.92, time + 0.04);
    const g = gain(ctx, 0);
    chain(osc, g).connect(pre);
    perc(g.gain, time, 0.001, decay, peak);
    osc.start(time);
    osc.stop(time + duration);
  }

  const wires = noiseSource(ctx, time, 0.4, 0x7c3af1d9);
  const wiresHp = biquad(ctx, 'highpass', 420, 0.7);
  const wiresBp = biquad(ctx, 'bandpass', 1700, 0.9);
  const wiresAir = biquad(ctx, 'lowpass', 6500, 0.7);
  const wiresGain = gain(ctx, 0);
  chain(wires, wiresHp, wiresBp, wiresAir, wiresGain).connect(pre);
  perc2(wiresGain.gain, time, 0.001, 0.06, 0.42, 0.28, 0.55);

  const rim = noiseSource(ctx, time, 0.03, 0x1b9e77c1);
  const rimHp = biquad(ctx, 'highpass', 3500, 0.7);
  const rimGain = gain(ctx, 0);
  // Drive 30 on 13 ms of noise: this is a clipped crack, and it is what makes the hit
  // audible over a riddim on a phone speaker.
  chain(rim, rimHp, shaper(ctx, 30), rimGain).connect(dest);
  perc(rimGain.gain, time, 0.0004, 0.015, 0.13);

  return duration;
}

/**
 * `synth:tom` — TOM.
 *
 * Two modes, not one sine: a drum with a single mode is a test tone. Pitched to 110 Hz so
 * that a phone speaker still moves, and both modes bend down through the hit because a
 * struck head loses tension as it decays.
 */
export function tom(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 0.42;
  const lp = biquad(ctx, 'lowpass', 900, 0.8);
  const sat = shaper(ctx, 2.2);
  chain(lp, sat).connect(dest);

  const modes: readonly (readonly [number, number, number])[] = [
    [110, 0.34, 0.7],
    [164, 0.16, 0.22],
  ];
  for (const [frequency, decay, peak] of modes) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    // 1.55× on the attack is the stick, not a pitch bend: the head is stiffest at the
    // moment of contact.
    osc.frequency.setValueAtTime(frequency * 1.55, time);
    osc.frequency.exponentialRampToValueAtTime(frequency, time + 0.09);
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.93, time + duration);
    const g = gain(ctx, 0);
    chain(osc, g).connect(lp);
    perc(g.gain, time, 0.001, decay, peak);
    osc.start(time);
    osc.stop(time + duration);
  }

  const head = noiseSource(ctx, time, 0.06, 0x8f2b4d17);
  const headLp = biquad(ctx, 'lowpass', 1600, 0.7);
  const headGain = gain(ctx, 0);
  chain(head, headLp, headGain).connect(lp);
  perc(headGain.gain, time, 0.0004, 0.035, 0.6);

  return duration;
}

/**
 * `synth:cowbell` — COWBELL.
 *
 * The 808's two squares at 540 and 800 Hz through a bandpass. Those are the values off
 * the schematic, so this is not an imitation of the sound — it **is** the sound. The
 * beater is the one addition: the 808 has no beater and every record made with one has a
 * transient there anyway, from the desk.
 */
export function cowbell(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 0.36;
  const bp = biquad(ctx, 'bandpass', 2640, 1.4);
  const sat = shaper(ctx, 3.5);
  const trim = gain(ctx, 0.52);
  const mix = gain(ctx, 0);
  chain(mix, bp, sat, trim).connect(dest);

  const voices: readonly (readonly [number, number, number])[] = [
    [540, -5, 0.5],
    [800, 6, 0.4],
  ];
  for (const [frequency, cents, level] of voices) {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = frequency;
    // A few cents apart so the pair beats slowly instead of locking into one timbre.
    osc.detune.value = cents;
    const g = gain(ctx, level);
    chain(osc, g).connect(mix);
    osc.start(time);
    osc.stop(time + duration);
  }

  const beater = noiseSource(ctx, time, 0.02, 0x4a7d2e15);
  const beaterBp = biquad(ctx, 'bandpass', 4200, 1.2);
  const beaterGain = gain(ctx, 0);
  chain(beater, beaterBp, beaterGain).connect(mix);
  perc(beaterGain.gain, time, 0.0003, 0.008, 0.35);

  perc2(mix.gain, time, 0.0008, 0.05, 0.42, 0.36, 0.8);
  return duration;
}

/**
 * `synth:bass` — BASS DROP, 110 → 28 Hz.
 *
 * A phone reproduces none of that. The parallel saturated copy highpassed at 150 Hz
 * supplies the harmonics the ear uses to infer the fundamental it cannot hear, and
 * without it the pad is *silent* on the device most of the audience is holding — which
 * reads to the user as a broken pad, not as a quiet one.
 */
export function bassDrop(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const duration = 1.2;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(110, time);
  osc.frequency.exponentialRampToValueAtTime(28, time + 0.9);
  osc.frequency.exponentialRampToValueAtTime(24, time + duration);

  const g = gain(ctx, 0);
  osc.connect(g);
  g.gain.setValueAtTime(0, time);
  g.gain.linearRampToValueAtTime(0.55, time + 0.015);
  g.gain.setValueAtTime(0.55, time + 0.55);
  g.gain.exponentialRampToValueAtTime(1e-4, time + duration);
  osc.start(time);
  osc.stop(time + duration + 0.02);

  g.connect(dest);
  const ghost = shaper(ctx, 9);
  const ghostHp = biquad(ctx, 'highpass', 150, 0.7);
  const ghostLp = biquad(ctx, 'lowpass', 1400, 0.7);
  const ghostGain = gain(ctx, 0.5);
  chain(g, ghost, ghostHp, ghostLp, ghostGain).connect(dest);

  return duration;
}

/* ── the brake's chord ──────────────────────────────────────────────────────────── */

const chordCache = new Map<number, AudioBuffer>();

/** How much material the brake has to decelerate. Longer than the stop, deliberately. */
export const CHORD_BUFFER_S = 0.4;

/**
 * A short band-limited chord, generated arithmetically so it is identical every render.
 *
 * Only {@link tapeStop} uses it. Partials are capped at 3200 Hz per note rather than at a
 * fixed count: the point of the cap is that nothing aliases when the playback rate falls
 * to zero, and that is a frequency condition, not a harmonic-number one.
 */
export function chordBuffer(ctx: BaseAudioContext): AudioBuffer {
  const hit = chordCache.get(ctx.sampleRate);
  if (hit !== undefined) return hit;

  const rate = ctx.sampleRate;
  const frames = Math.ceil(rate * CHORD_BUFFER_S);
  const buffer = ctx.createBuffer(1, frames, rate);
  const data = buffer.getChannelData(0);
  for (const frequency of [110, 130.81, 164.81, 220]) {
    const partials = Math.max(1, Math.floor(3200 / frequency));
    for (let h = 1; h <= partials; h += 1) {
      const amplitude = 0.16 / h;
      const omega = (2 * Math.PI * frequency * h) / rate;
      /* `noUncheckedIndexedAccess` is on: a Float32Array index is `number | undefined`
         to the compiler even though it never is at runtime. */
      for (let i = 0; i < frames; i += 1)
        data[i] = (data[i] ?? 0) + amplitude * Math.sin(omega * i);
    }
  }
  chordCache.set(rate, buffer);
  return buffer;
}

/**
 * `synth:brake` — TAPE STOP.
 *
 * Deceleration is `(1 - t/T)^1.6`, which is capstan friction. Linear is the giveaway: it
 * sounds like a pitch bend, not like a hand on a reel. The lowpass falls with the rate
 * because head-to-tape speed **is** bandwidth on a real machine — the two are the same
 * physical quantity, so animating one without the other reads as an effect.
 *
 * **Note on what this brakes.** In the prototype this voice sat on the demo riddim's own
 * bus, so it braked the tune. In the app the deck is an `HTMLAudioElement` that this
 * context never meets by design (spec.md §13.2, and see `engine.ts`), so BRAKE renders
 * its own decelerating chord on the pad bus and the screen mutes the deck underneath it —
 * that is what `planDeckDuck` in `engine.ts` is for.
 */
export function tapeStop(ctx: BaseAudioContext, dest: AudioNode, time: number): number {
  const stop = 0.62;
  const duration = 0.7;

  const src = ctx.createBufferSource();
  src.buffer = chordBuffer(ctx);
  src.loop = true;

  const lp = biquad(ctx, 'lowpass', 12000, 0.7);
  const sat = shaper(ctx, 2.2);
  const dc = biquad(ctx, 'highpass', 30, 0.7);
  const g = gain(ctx, 0.55);
  chain(src, lp, sat, dc, g).connect(dest);

  // 128 points over 620 ms is a point every 4.8 ms — finer than the ear resolves as a
  // step, and `setValueCurveAtTime` interpolates between them anyway.
  const points = 128;
  const curve = new Float32Array(points);
  for (let i = 0; i < points; i += 1) curve[i] = Math.pow(1 - i / (points - 1), 1.6);
  src.playbackRate.setValueCurveAtTime(curve, time, stop);

  // Wow on the way down: a reel being stopped by hand is not smooth.
  const wow = ctx.createOscillator();
  wow.type = 'sine';
  wow.frequency.value = 3.1;
  const wowDepth = gain(ctx, 0.012);
  wow.connect(wowDepth).connect(src.playbackRate);
  wow.start(time);
  wow.stop(time + stop);

  lp.frequency.setValueAtTime(12000, time);
  lp.frequency.exponentialRampToValueAtTime(700, time + stop);
  g.gain.setValueAtTime(0.55, time + stop - 0.05);
  g.gain.exponentialRampToValueAtTime(1e-4, time + duration);

  src.start(time);
  src.stop(time + duration + 0.02);
  return duration;
}

/* ══ the recipe table ════════════════════════════════════════════════════════════ */

/**
 * The seven `synth:<voice>` recipes, keyed by the contract's voice names.
 *
 * Typed as a total `Record` over `SynthVoice` on purpose: adding a name to
 * `SYNTH_VOICES` in `@bl/shared` without writing its recipe is then a compile error
 * rather than a pad that does nothing on a phone in a club.
 */
export const SYNTH_RECIPES: Readonly<Record<SynthVoice, PadVoice>> = {
  shot: lickShot,
  'one-drop': oneDropKick,
  snare: oneDropSnare,
  tom,
  cowbell,
  bass: bassDrop,
  brake: tapeStop,
};

/* ══ the siren ═══════════════════════════════════════════════════════════════════ */

/** The swept range, in Hz. Exponential between them — see {@link setSiren}. */
export const SIREN_LOW_HZ = 280;
export const SIREN_HIGH_HZ = 1500;

/** The warble range, in Hz. Vertical thumb position picks a point in it. */
export const SIREN_WARBLE_MIN_HZ = 3;
export const SIREN_WARBLE_MAX_HZ = 11;

/** How long the tail takes after the finger lifts, before the nodes are stopped. */
export const SIREN_RELEASE_S = 0.35;

export interface SirenVoice {
  readonly vca: GainNode;
  readonly oscs: readonly OscillatorNode[];
  readonly lfo: OscillatorNode;
  readonly lp1: BiquadFilterNode;
  readonly lp2: BiquadFilterNode;
}

/** What {@link setSiren} just did, so the screen can print it without reading the graph. */
export interface SirenReadout {
  readonly hz: number;
  readonly warbleHz: number;
}

/**
 * Starts the momentary siren into `dest`.
 *
 * This is an instrument, not a clip: it holds while the finger holds, and the thumb's
 * position is a control, not a trigger. Two detuned squares through a resonant lowpass —
 * the resonance *is* the box, a real dub siren is a small tin can with a speaker in it —
 * then attenuated **into** a shaper and highpassed to remove the DC the asymmetric curve
 * leaves behind.
 *
 * The `pre` gain of 0.15 is the lesson-1 rule applied by hand: 0.15 in, drive 3, and the
 * output sits where it belongs. Raising it after the shaper would just clip.
 */
export function startSiren(ctx: BaseAudioContext, dest: AudioNode, time: number): SirenVoice {
  const vca = gain(ctx, 0);
  const lp1 = biquad(ctx, 'lowpass', 900, 9);
  const lp2 = biquad(ctx, 'lowpass', 900, 0.7);
  const pre = gain(ctx, 0.15);
  const drive = shaper(ctx, 3);
  const dc = biquad(ctx, 'highpass', 120, 0.7);
  chain(vca, lp1, lp2, pre, drive, dc).connect(dest);

  const oscs: OscillatorNode[] = [];
  for (const cents of [-9, 9]) {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.detune.value = cents;
    osc.frequency.value = 520;
    osc.connect(vca);
    osc.start(time);
    oscs.push(osc);
  }

  // The warble is written into `detune`, not `frequency`, so it survives the
  // `setTargetAtTime` sweep on `frequency` without the two fighting over the same param.
  const lfo = ctx.createOscillator();
  lfo.type = 'triangle';
  lfo.frequency.value = 5.4;
  const lfoDepth = gain(ctx, 420);
  for (const osc of oscs) lfoDepth.connect(osc.detune);
  lfo.connect(lfoDepth);
  lfo.start(time);

  vca.gain.setValueAtTime(0, time);
  vca.gain.linearRampToValueAtTime(0.085, time + 0.02);

  return { vca, oscs, lfo, lp1, lp2 };
}

/** Clamps to `[0, 1]`, treating a non-finite input as 0 rather than as `NaN` downstream. */
function unit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Moves the live siren. `x` and `y` are fractions of the control, top-left origin.
 *
 * **The pitch map is exponential.** A linear map is the single most common reason a
 * synthesised siren sounds like a toy: pitch perception is logarithmic, so a linear sweep
 * spends most of its travel in the top octave and none of it where the interesting part
 * is. The filter tracks 2.2× above the tone so the box resonance moves with it instead of
 * sitting still and swallowing the top of the sweep.
 *
 * `setTargetAtTime` rather than a ramp: the finger sends a stream of positions and each
 * one has to bend towards its target without cancelling the last, which a ramp would.
 */
export function setSiren(
  ctx: BaseAudioContext,
  voice: SirenVoice,
  x: number,
  y: number,
): SirenReadout {
  const t = ctx.currentTime;
  const hz = SIREN_LOW_HZ * Math.pow(SIREN_HIGH_HZ / SIREN_LOW_HZ, unit(x));
  for (const osc of voice.oscs) osc.frequency.setTargetAtTime(hz, t, 0.02);
  voice.lp1.frequency.setTargetAtTime(hz * 2.2, t, 0.03);
  voice.lp2.frequency.setTargetAtTime(hz * 2.2, t, 0.03);

  // Up is fast: the top of the control is the panic end of the range.
  const warbleHz =
    SIREN_WARBLE_MIN_HZ + (1 - unit(y)) * (SIREN_WARBLE_MAX_HZ - SIREN_WARBLE_MIN_HZ);
  voice.lfo.frequency.setTargetAtTime(warbleHz, t, 0.05);

  return { hz, warbleHz };
}

/**
 * Releases the siren.
 *
 * `cancelScheduledValues` first, because the attack ramp may still be pending on a very
 * short tap and would otherwise fire *after* the release and leave the voice open. Then a
 * 30 ms exponential approach to zero — an instant zero is a click — and the oscillators
 * are stopped on the audio clock once the tail has finished, so nothing is left running
 * in a hidden tab.
 */
export function stopSiren(ctx: BaseAudioContext, voice: SirenVoice): void {
  const t = ctx.currentTime;
  voice.vca.gain.cancelScheduledValues(t);
  voice.vca.gain.setTargetAtTime(0, t, 0.03);
  sched(ctx, t + SIREN_RELEASE_S, () => {
    for (const osc of voice.oscs) {
      try {
        osc.stop();
      } catch {
        // Already stopped — a second release, or a teardown that got there first.
      }
    }
    try {
      voice.lfo.stop();
    } catch {
      // Idem.
    }
    voice.vca.disconnect();
  });
}

/* ══ cache lifecycle ═════════════════════════════════════════════════════════════ */

/**
 * Builds every cached buffer up front.
 *
 * Called **synchronously** while the context is being created, inside the gesture. Done
 * lazily on the first tap instead, this was measured at ~19 ms of main-thread jank — on a
 * 80 ms latency budget that is a quarter of it spent generating noise. Lesson 4.
 */
export function warmVoiceCaches(ctx: BaseAudioContext): void {
  for (const seed of NOISE_SEEDS) noiseBuffer(ctx, NOISE_BUFFER_S, seed);
  chordBuffer(ctx);
  // Every curve the seven voices and the two tanks ask for, so no tap ever fills a table.
  for (const k of [2.2, 2.4, 3, 3.5, 6, 7, 9, 30, 40]) driveCurve(k);
  limiterCurve();
}

/**
 * Drops the context-bound caches.
 *
 * `AudioBuffer`s are rendered at one context's sample rate and belong to it; keeping them
 * past `close()` would hand the next context stale audio and hold its memory. The curve
 * tables are **not** dropped — they are arithmetic on a number line, sample-rate
 * independent, and rebuilding them would reintroduce exactly the jank lesson 4 is about.
 */
export function resetVoiceCaches(): void {
  noiseCache.clear();
  chordCache.clear();
  noiseTurn = 0;
}
